import React, { useEffect, useState } from 'react';
import { DrawingAssignmentResult, RoomResult } from '@shared/api-dto';
import { DrawnTogetherPhase } from '@shared/constants';
import { quote } from '../shared/room-data-helpers';
import './drawn-together.css';

export const getSelectedPosterUrl = (assignment: DrawingAssignmentResult) => (
  assignment.generatedDrawings?.find(drawing => drawing.selected)?.url
);

export const getDrawingUrl = (assignment: DrawingAssignmentResult) => (
  assignment.drawing ? `/uploads/${assignment.drawing}` : undefined
);

type DrawnTogetherOverlayProps = {
  roomData: RoomResult;
  onShowcaseComplete: () => void;
};

/**
 * Big-screen content for a Drawn Together move, by phase:
 *  - drawing:    who has finished picking their poster
 *  - presenting: each poster revealed one at a time, fading in over the sketch
 *  - voting:     all posters side by side while players vote on their phones
 */
export const DrawnTogetherOverlay = ({ roomData, onShowcaseComplete }: DrawnTogetherOverlayProps) => {
  const move = roomData.currentMove!;
  const assignments = move.drawingAssignments ?? [];
  const phase = move.phase ?? DrawnTogetherPhase.DRAWING;

  if (phase === DrawnTogetherPhase.PRESENTING) {
    return (
      <PosterShowcase
        key={'showcase' + roomData.move}
        question={move.prompt.text}
        assignments={assignments.filter(getSelectedPosterUrl)}
        onComplete={onShowcaseComplete}
      />
    );
  }

  if (phase === DrawnTogetherPhase.VOTING) {
    const voteCount = assignments.reduce((sum, da) => sum + da.voterGuids.length, 0);
    return (
      <>
        <div className='moveTop'>
          <span className='chevrons statusPill'>Vote!</span>
        </div>
        <p className='moveKicker'>
          {move.submitted
            ? 'The votes are in...'
            : 'Pick your favorite poster on your phone'}
        </p>
        <div className='posterGallery'>
          {assignments.filter(getSelectedPosterUrl).map(assignment => (
            <figure className='galleryPoster' key={assignment.drawer.user.guid}>
              <img src={getSelectedPosterUrl(assignment)} alt='Movie poster' />
            </figure>
          ))}
        </div>
        {!move.submitted && (
          <p className='moveFooter'>
            {voteCount} of {roomData.players.length} votes in
          </p>
        )}
      </>
    );
  }

  const readyCount = assignments.filter(getSelectedPosterUrl).length;

  return (
    <>
      <div className='moveTop'>
        <span className='chevrons statusPill'>Everyone draws</span>
      </div>
      <div className='moveHeading'>
        <p className='moveKicker'>Turn a teammate&apos;s story into a movie poster</p>
        <h2 className='moveQuestion'>{move.prompt.text}</h2>
      </div>
      <ul className='drawerStatusList'>
        {assignments.map(assignment => {
          const ready = Boolean(getSelectedPosterUrl(assignment));
          const status = assignment.generatedDrawings?.length
            ? 'picking a poster...'
            : assignment.drawing
              ? 'generating posters...'
              : 'drawing...';
          const team = assignment.drawer.team === 2 ? 2 : 1;
          return (
            <li key={assignment.drawer.user.guid} className={ready ? 'done' : ''}>
              <span className={`avatar team${team}`}>
                {assignment.drawer.user.name.trim().charAt(0)}
              </span>
              <span className='drawerName'>{assignment.drawer.user.name}</span>
              {!ready && <span className='drawerStatus'>{status}</span>}
              {ready && <span className='drawerCheck' aria-hidden='true' />}
            </li>
          );
        })}
      </ul>
      <p className='moveFooter'>
        {readyCount} of {assignments.length} posters ready
      </p>
    </>
  );
};

// Each poster's reveal runs through these steps in order.
type ShowcaseStep = 'story' | 'drawing' | 'fade' | 'poster';
const SHOWCASE_STEPS: { step: ShowcaseStep; duration: number }[] = [
  { step: 'story', duration: 3500 }, // question + the answer that was drawn
  { step: 'drawing', duration: 2500 }, // the sketch on its own
  { step: 'fade', duration: 4500 }, // poster fades in over the sketch
  { step: 'poster', duration: 2500 }, // finished poster
];

const SLIDE_DURATION = 550;

type PosterShowcaseProps = {
  question: string;
  assignments: DrawingAssignmentResult[];
  onComplete: () => void;
};

type ExitingSlide = {
  assignment: DrawingAssignmentResult;
};

const PosterShowcase = ({ question, assignments, onComplete }: PosterShowcaseProps) => {
  const [index, setIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [exiting, setExiting] = useState<ExitingSlide | null>(null);

  useEffect(() => {
    if (assignments.length === 0) {
      onComplete();
      return;
    }

    const timeout = setTimeout(() => {
      if (stepIndex + 1 < SHOWCASE_STEPS.length) {
        setStepIndex(stepIndex + 1);
      } else if (index + 1 < assignments.length) {
        setExiting({ assignment: assignments[index] });
        setIndex(index + 1);
        setStepIndex(0);
      } else {
        onComplete();
      }
    }, SHOWCASE_STEPS[stepIndex].duration);

    return () => clearTimeout(timeout);
  }, [index, stepIndex, assignments.length]);

  useEffect(() => {
    if (!exiting) {
      return;
    }
    const timeout = setTimeout(() => setExiting(null), SLIDE_DURATION);
    return () => clearTimeout(timeout);
  }, [exiting]);

  const assignment = assignments[index];
  if (!assignment) {
    return null;
  }

  const step = SHOWCASE_STEPS[stepIndex].step;

  return (
    <div className='posterShowcase'>
      <p className='showcaseCounter'>{index + 1} of {assignments.length}</p>
      <p className='showcaseQuestion'><i>{question}</i></p>
      <div className='showcaseViewport'>
        {exiting && (
          <ShowcaseBody
            key={'exit-' + exiting.assignment.drawer.user.guid}
            assignment={exiting.assignment}
            step='poster'
            className='exit'
          />
        )}
        <ShowcaseBody
          key={assignment.drawer.user.guid}
          assignment={assignment}
          step={step}
          className={exiting ? 'enter' : undefined}
        />
      </div>
    </div>
  );
};

type ShowcaseBodyProps = {
  assignment: DrawingAssignmentResult;
  step: ShowcaseStep;
  className?: string;
};

const ShowcaseBody = ({ assignment, step, className }: ShowcaseBodyProps) => {
  const posterVisible = step === 'fade' || step === 'poster';
  const frameClass = step === 'story'
    ? 'hidden'
    : step === 'drawing'
      ? 'reveal'
      : 'shown';

  return (
    <div className={'showcaseBody' + (className ? ` ${className}` : '')}>
      <p className='showcaseAnswer'>
        {quote(assignment.responseToDraw.text)}
      </p>
      <div className={`showcaseFrame ${frameClass}`}>
        <img
          className='showcaseDrawing'
          src={getDrawingUrl(assignment)}
          alt='Sketch'
        />
        <img
          className={`showcasePoster${posterVisible ? ' visible' : ''}`}
          src={getSelectedPosterUrl(assignment)}
          alt='Movie poster'
        />
      </div>
    </div>
  );
};
